/**
 * cwear-cart.js
 * Premium cart interactions — CWEAR Theme
 *
 * Features:
 *   - AJAX quantity update (debounced)
 *   - AJAX remove item
 *   - Subtotal sync from /cart.js
 *   - Loading states per item
 *   - Empty state reveal (removes layout + footer + col-headers)
 *   - Cart icon bubble sync via Section Rendering API
 *
 * Rules:
 *   - Vanilla JS only. No jQuery.
 *   - No innerHTML with untrusted data.
 *   - No full page reloads.
 *   - Event delegation on stable parent.
 */

(function () {
  'use strict';

  // ─── Utility: format money from Shopify cents ─────────────────────────────────

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, '$ {{amount}}');
    }
    var amount = (cents / 100).toFixed(2);
    return '$\u00a0' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ─── Utility: debounce ────────────────────────────────────────────────────────

  function debounce(fn, delay) {
    var timer;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, delay);
    };
  }

  // ─── Cart singleton ───────────────────────────────────────────────────────────

  var CwearCart = {

    _pendingQty: {},
    _debouncedFlush: null,

    init: function () {
      var root = document.querySelector('.cwear-cart');
      if (!root) return;

      this._root = root;
      this._cacheRefs();

      this._debouncedFlush = debounce(this._flushQtyUpdates.bind(this), 320);

      this._bindEvents();
      initRecommended(this._root);
    },

    _cacheRefs: function () {
      this._itemsList  = document.getElementById('cwear-cart-items');
      this._footer     = document.getElementById('cwear-cart-footer');
      this._colHeaders = this._root.querySelector('.cwear-cart__col-headers');
      this._subtotalEl = this._root.querySelector('[data-subtotal]');
      this._emptyTpl   = document.getElementById('cwear-cart-empty-tpl');
    },

    /* Refresca la tabla de items + footer/subtotal desde el servidor via
       Section Rendering API — usado después de agregar un producto desde
       "Completa tu compra", que crea una fila nueva que el resto de los
       métodos (pensados para editar filas ya existentes en el DOM) no
       pueden construir solos.

       A propósito NO toca la grilla de "Completa tu compra": reemplazar
       toda la sección de un tirón (incluyendo esa grilla, que se vuelve a
       armar de cero pidiendo cada producto por separado) se sentía como
       un reload — un parpadeo de "todo desaparece y vuelve a aparecer".
       Solo se reemplazan items/headers/footer; los cards recomendados ya
       cargados quedan intactos. */
    refreshFromServer: function () {
      var self = this;
      var sectionId = this._root.dataset.sectionId;
      if (!sectionId) return Promise.resolve();

      return fetch('/?sections=' + sectionId)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var html = data[sectionId];
          if (!html) return;

          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newRoot = doc.querySelector('.cwear-cart');
          if (!newRoot) return;

          // Caso borde: el carrito estaba vacío (mostrando el estado
          // vacío, sin tabla) y esta es la primera vez que se agrega algo
          // desde "Completa tu compra" — ahí sí hace falta reemplazar
          // todo, porque no hay tabla que actualizar quirúrgicamente.
          if (!self._itemsList) {
            self._root.innerHTML = newRoot.innerHTML;
            self._cacheRefs();
            initRecommended(self._root);
            return;
          }

          var newItemsList  = doc.getElementById('cwear-cart-items');
          var newColHeaders = doc.querySelector('.cwear-cart__col-headers');
          var newFooter     = doc.getElementById('cwear-cart-footer');

          if (newItemsList && self._itemsList) {
            self._itemsList.replaceWith(newItemsList);
            self._itemsList = newItemsList;
          }
          if (newColHeaders && self._colHeaders) {
            self._colHeaders.replaceWith(newColHeaders);
            self._colHeaders = newColHeaders;
          }
          if (newFooter && self._footer) {
            self._footer.replaceWith(newFooter);
            self._footer = newFooter;
            self._subtotalEl = self._footer.querySelector('[data-subtotal]');
          }
        })
        .catch(function (err) {
          console.error('[CwearCart] refresh error:', err);
        });
    },

    _bindEvents: function () {
      var self = this;

      // Delegate on the whole root — covers items list + footer area
      this._root.addEventListener('click', function (e) {
        var buyNowBtn = e.target.closest('[data-buy-now]');
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
          buyNowBtn.textContent = 'Redirigiendo...';
          buyItemAlone(buyNowBtn.dataset.id, parseInt(buyNowBtn.dataset.qty, 10), buyNowBtn);
          return;
        }

        var btn = e.target.closest(
          '.cwear-cart__qty-btn, .cwear-cart__item-remove, .cwear-cart__item-remove-desktop'
        );
        if (!btn) return;

        var item = btn.closest('.cwear-cart__item');
        if (!item) return;

        var action = btn.dataset.action;

        if (action === 'increase' || action === 'decrease') {
          self._onQtyClick(btn, item, action);
        } else if (action === 'remove') {
          self._onRemoveClick(item);
        }
      });

      // Cart note autosave
      var note = document.querySelector('.cwear-cart__note-input');
      if (note) {
        note.addEventListener('change', debounce(function () {
          self._updateNote(note.value);
        }, 600));
      }
    },

    // ── Quantity click ──────────────────────────────────────────────────────────

    /* El item tiene DOS .cwear-cart__qty-val en el DOM al mismo tiempo
       (uno para la fila mobile, otro para la columna desktop — CSS
       oculta el que no aplica según el viewport, pero ambos existen
       siempre). Actualizar solo el que se clickeó deja al otro
       desactualizado; cuando _setLoading vuelve a habilitar los
       botones, lee "el primer .qty-val que encuentra" (el de mobile,
       que quedó en 1) y deja el "-" deshabilitado para siempre aunque
       la cantidad real ya sea 2+. Por eso ambos se sincronizan juntos
       acá, sea cual sea el que el usuario tocó. */
    _syncQtyDisplay: function (item, qty) {
      var display = qty < 10 ? '0' + qty : String(qty);
      item.querySelectorAll('.cwear-cart__qty-val').forEach(function (el) {
        el.textContent = display;
      });
      item.querySelectorAll('.cwear-cart__qty-btn--minus').forEach(function (b) {
        b.disabled = qty <= 1;
      });
    },

    _onQtyClick: function (btn, item, action) {
      // Find the qty-val closest to the button (handles both desktop col and mobile row)
      var qtyEl = btn.parentElement.querySelector('.cwear-cart__qty-val');
      if (!qtyEl) return;

      var current = parseInt(qtyEl.textContent, 10) || 1;

      // Restar desde 1 elimina el producto — mismo camino que el botón "Eliminar".
      if (action === 'decrease' && current <= 1) {
        this._onRemoveClick(item);
        return;
      }

      var next = action === 'increase' ? current + 1 : current - 1;
      if (next === current) return;

      // Optimistic UI — zero-padded, sincronizado en ambas copias del qty-val
      this._syncQtyDisplay(item, next);

      var key = item.dataset.itemKey;
      this._pendingQty[key] = next;
      this._debouncedFlush();
    },

    // ── Flush pending qty changes ───────────────────────────────────────────────

    _flushQtyUpdates: function () {
      var self = this;
      var pending = this._pendingQty;
      this._pendingQty = {};

      Object.keys(pending).forEach(function (key) {
        var qty = pending[key];
        var item = self._itemsList
          ? self._itemsList.querySelector('[data-item-key="' + key + '"]')
          : null;

        self._cartChange({ id: key, quantity: qty }, item, function (cart) {
          self._updateLinePrices(cart);
          self._syncSubtotalFromCart(cart);
        });
      });
    },

    // ── Remove click ────────────────────────────────────────────────────────────

    _onRemoveClick: function (item) {
      var self = this;
      var key = item.dataset.itemKey;
      this._setLoading(item, true);

      this._cartChange({ id: key, quantity: 0 }, null, function (cart) {
        item.style.transition = 'opacity 200ms ease, transform 200ms ease';
        item.style.opacity = '0';
        item.style.transform = 'translateY(-6px)';

        setTimeout(function () {
          item.remove();
          self._syncSubtotalFromCart(cart);
          if (cart.item_count === 0) {
            self._showEmptyState();
          }
        }, 210);
      });
    },

    // ── Core: POST /cart/change.js ──────────────────────────────────────────────

    _cartChange: function (payload, item, onSuccess) {
      var self = this;
      if (item) this._setLoading(item, true);

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart change failed: ' + res.status);
          return res.json();
        })
        .then(function (cart) {
          if (item) self._setLoading(item, false);
          if (onSuccess) onSuccess(cart);
        })
        .catch(function (err) {
          if (item) self._setLoading(item, false);
          self._onError(item, err);
        });
    },

    // ── Update line prices in DOM ───────────────────────────────────────────────

    _updateLinePrices: function (cart) {
      var list = this._itemsList;
      if (!list) return;

      cart.items.forEach(function (lineItem) {
        var el = list.querySelector('[data-item-key="' + lineItem.key + '"]');
        if (!el) return;
        var priceEl = el.querySelector('[data-line-price]');
        if (priceEl) priceEl.textContent = formatMoney(lineItem.final_line_price);
        var priceMobileEl = el.querySelector('[data-line-price-mobile]');
        if (priceMobileEl) priceMobileEl.textContent = formatMoney(lineItem.final_line_price);
      });
    },

    // ── Sync subtotal ───────────────────────────────────────────────────────────

    _syncSubtotalFromCart: function (cart) {
      if (this._subtotalEl) {
        this._subtotalEl.textContent = formatMoney(cart.total_price);
      }
      this._updateCartCount(cart.item_count);
    },

    // ── Update cart icon bubble ─────────────────────────────────────────────────

    _updateCartCount: function (count) {
      // Siempre actualizar data-cwear-cart-count del header cwear
      document.querySelectorAll('[data-cwear-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });

      // Dawn: cart-count-bubble via Section Rendering API
      var sections = 'cart-icon-bubble';
      fetch('/?sections=' + sections)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data['cart-icon-bubble']) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
            var newBubble = doc.querySelector('.cart-count-bubble');
            var currentBubble = document.querySelector('.cart-count-bubble');
            if (newBubble && currentBubble) {
              currentBubble.innerHTML = newBubble.innerHTML;
              if (count > 0) {
                currentBubble.removeAttribute('hidden');
              } else {
                currentBubble.setAttribute('hidden', '');
              }
            }
          }
        })
        .catch(function () {
          // Fallback Dawn manual
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = count;
          });
          document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
            if (count > 0) {
              el.removeAttribute('hidden');
              var inner = el.querySelector('[aria-hidden]');
              if (inner) inner.textContent = count;
            } else {
              el.setAttribute('hidden', '');
            }
          });
        });
    },

    // ── Note update ────────────────────────────────────────────────────────────

    _updateNote: function (note) {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note }),
      }).catch(function () {});
    },

    // ── Loading state ──────────────────────────────────────────────────────────

    _setLoading: function (item, state) {
      if (!item) return;
      if (state) {
        item.classList.add('is-loading');
        item.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
      } else {
        item.classList.remove('is-loading');
        // Cualquiera de los dos .qty-val sirve acá -- _onQtyClick ya los
        // mantiene sincronizados entre sí, así que leer uno solo es
        // seguro (antes se leía "el primero que aparece en el DOM", que
        // podía ser el de la fila mobile oculta y quedar desactualizado).
        var qtyEl = item.querySelector('.cwear-cart__qty-val');
        var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
        item.querySelectorAll('button').forEach(function (b) {
          b.disabled = b.dataset.action === 'decrease' ? qty <= 1 : false;
        });
      }
    },

    // ── Empty state ─────────────────────────────────────────────────────────────

    _showEmptyState: function () {
      var self = this;

      // Collect all elements to remove
      var toRemove = [
        this._colHeaders,
        this._itemsList,
        this._footer,
      ].filter(Boolean);

      // Fade out
      toRemove.forEach(function (el) {
        el.style.transition = 'opacity 240ms ease';
        el.style.opacity = '0';
      });

      setTimeout(function () {
        toRemove.forEach(function (el) { el.remove(); });

        // Show empty state from template
        if (self._emptyTpl) {
          var clone = self._emptyTpl.content.cloneNode(true);
          self._root.appendChild(clone);
        }
      }, 250);
    },

    // ── Error handler ──────────────────────────────────────────────────────────

    _onError: function (item, err) {
      console.error('[CwearCart]', err);
      var self = this;
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self._updateLinePrices(cart);
          self._syncSubtotalFromCart(cart);
          if (self._itemsList) {
            cart.items.forEach(function (lineItem) {
              var el = self._itemsList.querySelector('[data-item-key="' + lineItem.key + '"]');
              if (!el) return;
              self._syncQtyDisplay(el, lineItem.quantity);
            });
          }
        })
        .catch(function () {});
    },

  };

  // ─── "Completa tu compra" ───────────────────────────────────────────────────
  //
  // Misma card que cwear-related-products (imagen, swatches de color,
  // selector mini de talla, quick-add con validación) — reusa la misma
  // lógica de armado, pero trae cada producto via /products/<handle>.js
  // en vez de la API de recomendaciones (acá no hay un product.id de
  // contexto: /cart no es una PDP).

  var REC_CLASS_CARD = 'cwear-rp__card';
  var REC_CLASS_CARD_LINK = 'cwear-rp__card-link';
  var REC_CLASS_IMG_WRAP = 'cwear-rp__img-wrap';
  var REC_CLASS_QUICK_ADD_WRAP = 'cwear-rp__quick-add';
  var REC_CLASS_QUICK_ADD_BTN = 'cwear-rp__quick-add-btn';
  var REC_CLASS_INFO = 'cwear-rp__info';
  var REC_CLASS_TITLE = 'cwear-rp__title';
  var REC_CLASS_TITLE_SUB = 'cwear-rp__title-sub';
  var REC_CLASS_PRICE = 'cwear-rp__price';
  var REC_CLASS_PRICE_SALE = 'cwear-rp__price-sale';
  var REC_CLASS_PRICE_COMPARE = 'cwear-rp__price-compare';
  var REC_CLASS_SWATCHES = 'cwear-rp__swatches';
  var REC_CLASS_SWATCH = 'cwear-rp__swatch';
  var REC_CLASS_SWATCH_ACTIVE = 'is-active';
  var REC_CLASS_SELECTORS = 'cwear-rp__selectors';
  var REC_CLASS_SIZE_SELECT_WRAP = 'cwear-rp__size-wrap';
  var REC_CLASS_SIZE_SELECT = 'cwear-rp__size-select';
  var REC_CLASS_ERROR = 'cwear-rp__error';

  function initRecommended(cartRoot) {
    var grid = cartRoot.querySelector('[data-cwear-cart-rec-grid]');
    if (!grid) return;

    var handlesEl = cartRoot.querySelector('[data-cwear-cart-rec-handles]');
    var sectionId = cartRoot.dataset.sectionId;
    var handles = [];
    if (handlesEl) {
      try {
        handles = JSON.parse(handlesEl.textContent) || [];
      } catch (e) {
        handles = [];
      }
    }
    if (!handles.length) return;

    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (res) { return (res.ok ? res.json() : null); })
          .catch(function () { return null; });
      })
    ).then(function (products) {
      products.forEach(function (product) {
        if (!product || !product.available) return;
        grid.appendChild(buildRecCard(product, sectionId));
      });
    });
  }

  function getRecColorMap(sectionId) {
    var el = document.getElementById('cwear-cart-color-map-' + sectionId);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function getRecColorHex(colorMap, colorName) {
    var key = String(colorName || '').toLowerCase().replace(/[\s\-_]/g, '');
    return colorMap[key] || 'cccccc';
  }

  // ─── Título largo: mismo criterio que cwear-pdp-main / cwear-cart-modal
  //     / cwear-cart / cwear-related-products. ────────────────────────

  function splitRecTitle(title) {
    var emParts = title.split(' — ');
    if (emParts.length > 1) {
      return { main: emParts[0], sub: emParts.slice(1).join(' — ') };
    }
    var enParts = title.split(' – ');
    if (enParts.length > 1) {
      return { main: enParts[0], sub: enParts.slice(1).join(' – ') };
    }
    var words = title.split(' ');
    if (words.length > 4) {
      var half = Math.floor(words.length / 2);
      return { main: words.slice(0, half).join(' '), sub: words.slice(half).join(' ') };
    }
    return { main: title, sub: null };
  }

  function buildRecOptionMeta(product) {
    return (product.options || []).map(function (opt, idx) {
      var name = (opt && opt.name) || ('Opción ' + (idx + 1));
      var lower = name.toLowerCase();
      var values = [];
      (product.variants || []).forEach(function (v) {
        var val = v['option' + (idx + 1)];
        if (val && values.indexOf(val) === -1) values.push(val);
      });
      return {
        name: name,
        position: idx + 1,
        values: values,
        isColor: lower === 'color' || lower === 'colour'
      };
    });
  }

  function setRecImageSrc(img, srcOrObj) {
    var srcUrl = typeof srcOrObj === 'string' ? srcOrObj : (srcOrObj && srcOrObj.src);
    if (!srcUrl) return;
    var base = srcUrl.replace(/(\.[^.]+)(\?.*)?$/, '');
    var ext = (srcUrl.match(/\.[^.?]+/g) || ['.jpg']).pop();
    var query = (srcUrl.match(/\?.*$/) || [''])[0];
    img.src = base + '_600x' + ext + query;
    img.srcset = [
      base + '_375x' + ext + query + ' 375w',
      base + '_480x' + ext + query + ' 480w',
      base + '_600x' + ext + query + ' 600w',
      base + '_800x' + ext + query + ' 800w'
    ].join(', ');
    img.sizes = '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';
  }

  function buildRecCard(product, sectionId) {
    var colorMap = getRecColorMap(sectionId);

    var article = document.createElement('article');
    article.className = REC_CLASS_CARD;
    article.setAttribute('role', 'listitem');

    var link = document.createElement('a');
    link.className = REC_CLASS_CARD_LINK;
    link.href = product.url;
    link.setAttribute('aria-label', product.title);

    var imgWrap = document.createElement('div');
    imgWrap.className = REC_CLASS_IMG_WRAP;
    var imgEl = null;

    if (product.featured_image) {
      imgEl = document.createElement('img');
      imgEl.className = 'cwear-rp__img';
      imgEl.alt = product.featured_image.alt || product.title;
      imgEl.loading = 'lazy';
      setRecImageSrc(imgEl, product.featured_image);
      imgWrap.appendChild(imgEl);
    }

    var selections = {};
    var optionMeta = buildRecOptionMeta(product);

    optionMeta.forEach(function (opt) {
      if (opt.values.length === 1) selections[opt.name] = opt.values[0];
    });

    var errorEl = document.createElement('p');
    errorEl.className = REC_CLASS_ERROR;

    function findVariant() {
      return (product.variants || []).find(function (v) {
        return optionMeta.every(function (opt) {
          return v['option' + opt.position] === selections[opt.name];
        });
      }) || null;
    }

    /* Imagen a mostrar al elegir un color — mismo fix que
       cwear-related-products.js: busca cualquier variante con ese color
       que tenga foto propia, sin exigir que la talla ya esté elegida
       (findVariant exige match completo, y la talla arranca sin elegir
       a propósito). */
    function findColorImage(colorOptPosition, colorValue) {
      var match = (product.variants || []).find(function (v) {
        return v['option' + colorOptPosition] === colorValue && v.featured_image;
      });
      return match ? match.featured_image : null;
    }

    var qaWrap = document.createElement('div');
    qaWrap.className = REC_CLASS_QUICK_ADD_WRAP;
    qaWrap.setAttribute('aria-hidden', 'true');

    var qaBtn = document.createElement('button');
    qaBtn.className = REC_CLASS_QUICK_ADD_BTN;
    qaBtn.type = 'button';
    qaBtn.textContent = 'Agregar';

    qaWrap.appendChild(qaBtn);
    qaWrap.appendChild(errorEl);
    imgWrap.appendChild(qaWrap);

    qaBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      var missing = optionMeta.filter(function (opt) { return !selections[opt.name]; });
      if (missing.length) {
        errorEl.textContent = 'Seleccioná ' + missing.map(function (o) { return o.name.toLowerCase(); }).join(' y ');
        return;
      }

      var variant = findVariant();
      if (!variant || !variant.available) {
        errorEl.textContent = 'Esa combinación no está disponible';
        return;
      }

      errorEl.textContent = '';
      addRecToCart(qaBtn, variant.id);
    });

    link.appendChild(imgWrap);

    var selectorsWrap = null;
    var colorOpt = optionMeta.find(function (o) { return o.isColor; });
    var otherOpts = optionMeta.filter(function (o) { return !o.isColor && o.values.length > 1; });

    if ((colorOpt && colorOpt.values.length > 1) || otherOpts.length) {
      selectorsWrap = document.createElement('div');
      selectorsWrap.className = REC_CLASS_SELECTORS;
    }

    var info = document.createElement('div');
    info.className = REC_CLASS_INFO;

    var titleSplit = splitRecTitle(product.title);
    var titleEl = document.createElement('p');
    titleEl.className = REC_CLASS_TITLE;
    titleEl.textContent = titleSplit.main;
    info.appendChild(titleEl);

    if (titleSplit.sub) {
      var titleSubEl = document.createElement('p');
      titleSubEl.className = REC_CLASS_TITLE_SUB;
      titleSubEl.textContent = titleSplit.sub;
      info.appendChild(titleSubEl);
    }

    var priceEl = document.createElement('p');
    priceEl.className = REC_CLASS_PRICE;

    if (product.compare_at_price && product.compare_at_price > product.price) {
      var saleSpan = document.createElement('span');
      saleSpan.className = REC_CLASS_PRICE_SALE;
      saleSpan.textContent = formatMoney(product.price);

      var compareSpan = document.createElement('span');
      compareSpan.className = REC_CLASS_PRICE_COMPARE;
      compareSpan.textContent = formatMoney(product.compare_at_price);

      priceEl.appendChild(saleSpan);
      priceEl.appendChild(compareSpan);
    } else {
      priceEl.textContent = formatMoney(product.price);
    }

    info.appendChild(priceEl);

    link.appendChild(info);
    article.appendChild(link);

    // Se llenan al construir cada <select> de talla más abajo; el click
    // de un swatch de color las vuelve a llamar para re-evaluar qué
    // tallas siguen disponibles con el nuevo color -- así el usuario
    // nunca puede elegir una combinación que no existe en stock.
    var refreshSizeAvailability = [];

    if (selectorsWrap) {
      if (colorOpt && colorOpt.values.length > 1) {
        var swatchesEl = document.createElement('div');
        swatchesEl.className = REC_CLASS_SWATCHES;
        swatchesEl.setAttribute('aria-label', 'Colores disponibles');

        colorOpt.values.slice(0, 5).forEach(function (color, idx) {
          var swatch = document.createElement('button');
          swatch.type = 'button';
          swatch.className = REC_CLASS_SWATCH;
          if (idx === 0) {
            swatch.classList.add(REC_CLASS_SWATCH_ACTIVE);
            selections[colorOpt.name] = color;
          }
          swatch.title = color;
          swatch.setAttribute('aria-label', color);
          swatch.style.backgroundColor = '#' + getRecColorHex(colorMap, color);
          swatch.addEventListener('click', function () {
            selections[colorOpt.name] = color;
            errorEl.textContent = '';
            Array.prototype.forEach.call(swatchesEl.children, function (s) {
              s.classList.remove(REC_CLASS_SWATCH_ACTIVE);
            });
            swatch.classList.add(REC_CLASS_SWATCH_ACTIVE);

            var colorImage = findColorImage(colorOpt.position, color);
            if (colorImage && imgEl) {
              setRecImageSrc(imgEl, colorImage.src || colorImage);
            }

            refreshSizeAvailability.forEach(function (fn) { fn(); });
          });
          swatchesEl.appendChild(swatch);
        });

        selectorsWrap.appendChild(swatchesEl);
      }

      otherOpts.forEach(function (opt) {
        var selectWrap = document.createElement('div');
        selectWrap.className = REC_CLASS_SIZE_SELECT_WRAP;

        var select = document.createElement('select');
        select.className = REC_CLASS_SIZE_SELECT;
        select.setAttribute('aria-label', opt.name);

        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = opt.name;
        select.appendChild(placeholder);

        opt.values.forEach(function (value) {
          var optionEl = document.createElement('option');
          optionEl.value = value;
          optionEl.textContent = value;
          select.appendChild(optionEl);
        });

        /* Deshabilita (sin ocultar) las tallas sin stock para el color
           elegido -- a pedido explícito del usuario, tras reportar que
           era frustrante elegir una talla y recién al intentar agregar
           enterarse de que no había. */
        function updateSizeAvailability() {
          var colorValue = colorOpt ? selections[colorOpt.name] : null;
          Array.prototype.forEach.call(select.options, function (optionEl) {
            if (!optionEl.value) return;
            var available = (product.variants || []).some(function (v) {
              if (v['option' + opt.position] !== optionEl.value) return false;
              if (colorOpt && colorValue && v['option' + colorOpt.position] !== colorValue) return false;
              return v.available;
            });
            optionEl.disabled = !available;
          });
        }
        updateSizeAvailability();
        refreshSizeAvailability.push(updateSizeAvailability);

        select.addEventListener('change', function () {
          selections[opt.name] = select.value || undefined;
          errorEl.textContent = '';
        });

        selectWrap.appendChild(select);
        selectorsWrap.appendChild(selectWrap);
      });

      article.appendChild(selectorsWrap);
    }

    return article;
  }

  function addRecToCart(btn, variantId) {
    if (btn.disabled) return;
    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = '...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        return fetch('/cart.js');
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        CwearCart._updateCartCount(cart.item_count);
        return CwearCart.refreshFromServer();
      })
      .then(function () {
        /* Éxito: el botón sigue existiendo (refreshFromServer NO toca
           "Completa tu compra" a propósito, ver comentario en
           refreshFromServer) -- si no se reactiva acá queda
           "..."/disabled para siempre, y el próximo click no hace
           nada porque la primera línea de esta función corta la
           ejecución cuando btn.disabled es true. Reportado por el
           usuario: el botón se quedaba trabado en "..." y un segundo
           click "no agregaba nada" hasta refrescar la página. */
        btn.textContent = originalText;
        btn.disabled = false;
      })
      .catch(function (err) {
        console.warn('[cwear-cart] Error agregando recomendado:', err);
        btn.textContent = originalText;
        btn.disabled = false;
      });
  }

  // ─── "Comprar solo esto" — mismo flujo que cwear-cart-modal.js: vacía
  //     el carrito dejando solo esta variante y va directo a /checkout.
  //     El resto se respalda en la MISMA clave de localStorage que usa el
  //     modal, cuya restauración automática ya corre en cada carga de
  //     página (cwear-cart-modal.js se renderiza global) -- no hace falta
  //     duplicar esa lógica acá. ───────────────────────────────────────

  var BUY_ALONE_BACKUP_KEY = 'cwearCartBackup';

  function buyItemAlone(variantId, quantity, btn) {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var backup = cart.items
          .filter(function (item) { return String(item.variant_id) !== String(variantId); })
          .map(function (item) { return { id: item.variant_id, quantity: item.quantity }; });

        try {
          if (backup.length) {
            localStorage.setItem(BUY_ALONE_BACKUP_KEY, JSON.stringify(backup));
          } else {
            localStorage.removeItem(BUY_ALONE_BACKUP_KEY);
          }
        } catch (e) {
          console.error('Error guardando respaldo del carrito:', e);
        }

        return fetch('/cart/clear.js', { method: 'POST' });
      })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
        });
      })
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        console.error('Error en compra directa:', err);
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Comprar solo esto';
        }
      });
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { CwearCart.init(); });
  } else {
    CwearCart.init();
  }

})();
